import React, { useState, useEffect, useCallback } from 'react'

import {
  BoxContainer,
  Button,
  CircleLoading,
  Loading,
  Typography
} from '../../../../components'
import Trash from '../../../../assets/Icons/Trash'
import ecbrApi from '../../../../services/ecbrApi'
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination'
import { UserEvents } from '../constants'
import { errorResponseStatusHandler } from '../../../../utils/handlers/errorResponseStatusHandler'
import { ecbrAdminRoleListForSelect } from '../../../../constants/roles'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import { getDate, getHour } from '../../../../utils/dateUtils'
import { AlertModal } from '../../../../modals/AlertModal'
import { useModal } from '../../../../hooks/useModal'
import { MainLoading } from '../../../../components/CircleLoading/styles'
import { Content } from '../../../../components/Content'
import { ModalBodyContainer } from '../../../../modals/styles'
import { ComponentTitle } from '../../styles'

const pagination = { page: 1, take: 5 }

const PendingInvites = ({ eventListener }) => {
  const [error, setError] = useState('')
  const [alertModal, openAlertModal] = useModal(AlertModal)
  const [isSending, setIsSending] = useState(false)
  const [searchParameters, _setSearchParameters] = useState({
    ...pagination
  })

  const openAlert = useCallback(
    (txt) =>
      openAlertModal({
        title: `Dados salvos!`,
        body: (
          <ModalBodyContainer>
            <Typography type="text" color="#7D7987">
              {txt}
            </Typography>
          </ModalBodyContainer>
        )
      }),
    [openAlertModal]
  )

  const {
    data: pendingInvites,
    fetchNextPage,
    hasNextPage,
    isLastPage,
    isFetching,
    isLoading,
    refetch
  } = useInfinitePagination(
    'pendingInvites',
    async (params) => {
      const { pageParam = searchParameters.page } = params
      return ecbrApi.userInvites.getPendingAdminInvites({
        ...searchParameters,
        page: pageParam || searchParameters.page
      })
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  useEffect(() => {
    eventListener.subscribe(UserEvents.INVITE_SENT, refetch)
    eventListener.subscribe(UserEvents.INVITE_DELETED, refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleReinvite = async (invite) => {
    setIsSending(true)
    try {
      setError('')
      await ecbrApi.userInvites.reinvite(invite.id)
      openAlert('Convite reenviado com sucesso!')
      setIsSending(false)
    } catch (err) {
      errorResponseStatusHandler(err, setError)
      setIsSending(false)
    }

    eventListener?.publish(UserEvents.INVITE_SENT)
  }

  const handleDelete = async (invite) => {
    const removeInvite = window.confirm(
      'Deseja realmente remover esse convite da lista?'
    )
    if (removeInvite) {
      try {
        setError('')
        await ecbrApi.userInvites.delete(invite.id)
        openAlert('Convite removido!')
      } catch (err) {
        errorResponseStatusHandler(err, setError)
      }

      eventListener?.publish(UserEvents.INVITE_DELETED)
    }
  }

  return (
    <BoxContainer>
      {alertModal}
      <ComponentTitle>
        Convites enviados
        {(isLoading || isFetching) && <Loading />}
      </ComponentTitle>

      <Content templateColumns="15% 16% 18% 10% 10% 6% 13%">
        <header>
          <ul>
            <li>Nome do usuário</li>
            <li>E-mail de acesso</li>
            <li>Perfil</li>
            <li>Data de envio</li>
            <li>Hora de envio</li>
            <li>Ações</li>
            <li>&nbsp;</li>
          </ul>
        </header>
        {isSending ? (
          <MainLoading>
            <CircleLoading />
          </MainLoading>
        ) : (
          <main>
            {pendingInvites?.map?.((invite) => (
              <ul key={invite.id}>
                <li>{invite.name}</li>
                <li>{invite.email}</li>
                <li>
                  {ecbrAdminRoleListForSelect.find(
                    (role) => role.value === invite.role
                  )?.label || '-'}
                </li>
                <li>{getDate(invite.createdAt)}</li>
                <li>{getHour(invite.createdAt)}</li>
                <li>
                  <div onClick={() => handleDelete(invite)}>
                    <Trash color="#858585" />
                  </div>
                </li>
                <li>
                  <Button
                    type="button"
                    variant="outlinedLink"
                    text="Reenviar convite"
                    onClick={() => handleReinvite(invite)}
                  />
                </li>
              </ul>
            ))}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {hasNextPage && !isLastPage && (
              <Button
                type="button"
                variant="outlined"
                text={isLoading || isFetching ? 'Carregando...' : 'Ver mais'}
                width="120px"
                onClick={() => fetchNextPage()}
              />
            )}
          </main>
        )}
      </Content>
    </BoxContainer>
  )
}

export default PendingInvites
